import React, { useCallback, useEffect, useRef, useState } from 'react';
import { safeHref } from '../../utils/sanitizeHtml';

export interface MentionUser {
  id: string;
  name: string;
  email?: string;
}

interface CommentRichEditorProps {
  value: string;
  onChange: (html: string) => void;
  placeholder?: string;
  minHeight?: string;
  disabled?: boolean;
  mentionUsers?: MentionUser[];
}

const exec = (command: string, value?: string) => {
  document.execCommand(command, false, value);
};

const IconBold = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M6 4h8a4 4 0 0 1 4 4 4 4 0 0 1-4 4H6z" /><path d="M6 12h9a4 4 0 0 1 4 4 4 4 0 0 1-4 4H6z" /></svg>
);
const IconItalic = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="19" y1="4" x2="10" y2="4" /><line x1="14" y1="20" x2="5" y2="20" /><line x1="15" y1="4" x2="9" y2="20" /></svg>
);
const IconUnderline = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M6 3v7a6 6 0 0 0 12 0V3" /><line x1="4" y1="21" x2="20" y2="21" /></svg>
);
const IconListBullet = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="8" y1="6" x2="21" y2="6" /><line x1="8" y1="12" x2="21" y2="12" /><line x1="8" y1="18" x2="21" y2="18" /><line x1="3" y1="6" x2="3.01" y2="6" /><line x1="3" y1="12" x2="3.01" y2="12" /><line x1="3" y1="18" x2="3.01" y2="18" /></svg>
);
const IconListOrdered = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="10" y1="6" x2="21" y2="6" /><line x1="10" y1="12" x2="21" y2="12" /><line x1="10" y1="18" x2="21" y2="18" /><path d="M4 6h1v4" /><path d="M4 10h2" /><path d="M6 18H4c0-1 2-2 2-3s-1-1.5-2-1" /></svg>
);
const IconAlignLeft = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="21" y1="6" x2="3" y2="6" /><line x1="15" y1="12" x2="3" y2="12" /><line x1="17" y1="18" x2="3" y2="18" /></svg>
);
const IconAlignCenter = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="21" y1="6" x2="3" y2="6" /><line x1="17" y1="12" x2="7" y2="12" /><line x1="19" y1="18" x2="5" y2="18" /></svg>
);
const IconAlignRight = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="21" y1="6" x2="3" y2="6" /><line x1="21" y1="12" x2="9" y2="12" /><line x1="21" y1="18" x2="7" y2="18" /></svg>
);
const IconLink = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M10 13a5 5 0 0 0 7.54.54l1.83-1.83a5 5 0 0 0-7.07-7.07l-1.09 1.09" /><path d="M14 11a5 5 0 0 0-7.54-.54L4.63 12.3a5 5 0 0 0 7.07 7.07l1.09-1.09" /></svg>
);

const closestAnchor = (node: Node | null, root: HTMLElement): HTMLAnchorElement | null => {
  let current: Node | null = node;
  while (current && current !== root) {
    if (current instanceof HTMLAnchorElement) return current;
    current = current.parentNode;
  }
  return null;
};

const decorateLink = (anchor: HTMLAnchorElement, href: string) => {
  anchor.setAttribute('href', href);
  anchor.setAttribute('target', '_blank');
  anchor.setAttribute('rel', 'noopener noreferrer');
};

function getMentionQueryAtCaret(editor: HTMLElement): { query: string; range: Range } | null {
  const sel = window.getSelection();
  if (!sel || sel.rangeCount === 0 || !sel.isCollapsed) return null;
  const range = sel.getRangeAt(0);
  if (!editor.contains(range.startContainer)) return null;

  const probe = range.cloneRange();
  probe.selectNodeContents(editor);
  probe.setEnd(range.startContainer, range.startOffset);
  const textBefore = probe.toString();
  const match = textBefore.match(/(^|[\s\u00A0])@([^\s@]*)$/);
  if (!match) return null;

  const query = match[2] || '';
  const atIndexInBefore = textBefore.length - query.length - 1;
  const walker = document.createTreeWalker(editor, NodeFilter.SHOW_TEXT);
  let seen = 0;
  let startNode: Text | null = null;
  let startOffset = 0;
  let endNode: Text | null = null;
  let endOffset = 0;
  let node: Node | null;
  while ((node = walker.nextNode())) {
    const text = node.textContent || '';
    const next = seen + text.length;
    if (!startNode && atIndexInBefore >= seen && atIndexInBefore < next) {
      startNode = node as Text;
      startOffset = atIndexInBefore - seen;
    }
    if (!endNode && textBefore.length >= seen && textBefore.length <= next) {
      endNode = node as Text;
      endOffset = textBefore.length - seen;
      break;
    }
    seen = next;
  }
  if (!startNode || !endNode) return null;

  const mentionRange = document.createRange();
  mentionRange.setStart(startNode, startOffset);
  mentionRange.setEnd(endNode, endOffset);
  return { query, range: mentionRange };
}

const CommentRichEditor: React.FC<CommentRichEditorProps> = ({
  value,
  onChange,
  placeholder = 'Write a comment...',
  minHeight = '100px',
  disabled = false,
  mentionUsers = []
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const skipNextExternalSync = useRef(false);
  const [mentionOpen, setMentionOpen] = useState(false);
  const [mentionQuery, setMentionQuery] = useState('');
  const [mentionIndex, setMentionIndex] = useState(0);
  const mentionRangeRef = useRef<Range | null>(null);
  const savedRangeRef = useRef<Range | null>(null);
  const linkInputRef = useRef<HTMLInputElement>(null);
  const [linkOpen, setLinkOpen] = useState(false);
  const [linkUrl, setLinkUrl] = useState('');
  const [linkText, setLinkText] = useState('');
  const [editingAnchor, setEditingAnchor] = useState(false);
  const [selectionCollapsed, setSelectionCollapsed] = useState(true);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (skipNextExternalSync.current) {
      skipNextExternalSync.current = false;
      return;
    }
    const next = value || '';
    if (el.innerHTML !== next) el.innerHTML = next;
  }, [value]);

  const emitChange = useCallback(() => {
    skipNextExternalSync.current = true;
    onChange(ref.current?.innerHTML ?? '');
  }, [onChange]);

  const filteredMentions = mentionUsers
    .filter((person) => {
      const query = mentionQuery.toLowerCase();
      if (!query) return true;
      return person.name.toLowerCase().includes(query) || (person.email || '').toLowerCase().includes(query);
    })
    .slice(0, 8);

  const updateMentionState = useCallback(() => {
    const el = ref.current;
    if (!el || !mentionUsers.length) {
      setMentionOpen(false);
      return;
    }
    const found = getMentionQueryAtCaret(el);
    if (!found) {
      setMentionOpen(false);
      mentionRangeRef.current = null;
      return;
    }
    mentionRangeRef.current = found.range;
    setMentionQuery(found.query);
    setMentionIndex(0);
    setMentionOpen(true);
  }, [mentionUsers.length]);

  const insertMention = useCallback(
    (person: MentionUser) => {
      const el = ref.current;
      const mentionRange = mentionRangeRef.current;
      if (!el || !mentionRange) return;
      const sel = window.getSelection();
      if (!sel) return;
      sel.removeAllRanges();
      sel.addRange(mentionRange);
      mentionRange.deleteContents();
      const span = document.createElement('span');
      span.className = 'comment-mention';
      span.setAttribute('data-user-id', person.id);
      span.setAttribute('data-user-name', person.name);
      span.contentEditable = 'false';
      span.textContent = `@${person.name}`;
      mentionRange.insertNode(span);
      const space = document.createTextNode('\u00A0');
      span.after(space);
      const after = document.createRange();
      after.setStartAfter(space);
      after.collapse(true);
      sel.removeAllRanges();
      sel.addRange(after);
      setMentionOpen(false);
      mentionRangeRef.current = null;
      el.focus();
      emitChange();
    },
    [emitChange]
  );

  const restoreSelection = useCallback(() => {
    const el = ref.current;
    const range = savedRangeRef.current;
    if (!el || !range) return null;
    el.focus();
    const sel = window.getSelection();
    if (!sel) return null;
    sel.removeAllRanges();
    sel.addRange(range);
    return range;
  }, []);

  const closeLinkBar = useCallback(() => {
    setLinkOpen(false);
    setLinkUrl('');
    setLinkText('');
    setEditingAnchor(false);
    savedRangeRef.current = null;
    ref.current?.focus();
  }, []);

  const openLinkBar = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    el.focus();
    const sel = window.getSelection();
    const range = sel && sel.rangeCount > 0 ? sel.getRangeAt(0).cloneRange() : null;
    if (range && el.contains(range.commonAncestorContainer)) {
      savedRangeRef.current = range;
      const anchor = closestAnchor(range.commonAncestorContainer, el);
      setEditingAnchor(Boolean(anchor));
      setLinkUrl(anchor?.getAttribute('href') || '');
      setLinkText(range.toString());
      setSelectionCollapsed(range.collapsed && !anchor);
    } else {
      savedRangeRef.current = null;
      setEditingAnchor(false);
      setLinkUrl('');
      setLinkText('');
      setSelectionCollapsed(true);
    }
    setLinkOpen(true);
    setTimeout(() => linkInputRef.current?.focus(), 0);
  }, []);

  const applyLink = useCallback(() => {
    const el = ref.current;
    const href = safeHref(linkUrl);
    if (!el || !href) return;
    const range = restoreSelection();
    const sel = window.getSelection();
    if (!sel) return;
    const existing = range ? closestAnchor(range.commonAncestorContainer, el) : null;
    if (existing) {
      decorateLink(existing, href);
    } else if (!range || range.collapsed) {
      const a = document.createElement('a');
      decorateLink(a, href);
      a.textContent = linkText.trim() || href;
      const insertAt = range || document.createRange();
      if (!range) {
        insertAt.selectNodeContents(el);
        insertAt.collapse(false);
      }
      insertAt.insertNode(a);
      const after = document.createRange();
      after.setStartAfter(a);
      after.collapse(true);
      sel.removeAllRanges();
      sel.addRange(after);
    } else {
      exec('createLink', href);
      el.querySelectorAll('a[href]').forEach((node) => {
        const a = node as HTMLAnchorElement;
        const safe = safeHref(a.getAttribute('href') || '');
        if (safe) decorateLink(a, safe);
      });
    }
    emitChange();
    closeLinkBar();
  }, [closeLinkBar, emitChange, linkText, linkUrl, restoreSelection]);

  const removeLink = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    restoreSelection();
    exec('unlink');
    emitChange();
    closeLinkBar();
  }, [closeLinkBar, emitChange, restoreSelection]);

  const wrapSelectionInTag = useCallback((tagName: string) => {
    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0) return;
    const range = sel.getRangeAt(0);
    if (range.collapsed) {
      const el = document.createElement(tagName);
      el.appendChild(document.createTextNode('\u00A0'));
      range.insertNode(el);
      range.setStart(el, 0);
      range.collapse(true);
      sel.removeAllRanges();
      sel.addRange(range);
    } else {
      const el = document.createElement(tagName);
      try {
        range.surroundContents(el);
      } catch {
        el.appendChild(range.extractContents());
        range.insertNode(el);
      }
      range.selectNodeContents(el);
      sel.removeAllRanges();
      sel.addRange(range);
    }
  }, []);

  const trigger = (command: string) => {
    const el = ref.current;
    if (!el) return;
    el.focus();
    if (command === 'italic') wrapSelectionInTag('em');
    else exec(command);
    emitChange();
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (mentionOpen && filteredMentions.length > 0) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setMentionIndex((i) => (i + 1) % filteredMentions.length);
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setMentionIndex((i) => (i - 1 + filteredMentions.length) % filteredMentions.length);
        return;
      }
      if (e.key === 'Enter' || e.key === 'Tab') {
        e.preventDefault();
        insertMention(filteredMentions[mentionIndex]);
        return;
      }
      if (e.key === 'Escape') {
        e.preventDefault();
        setMentionOpen(false);
        return;
      }
    }
    if (e.ctrlKey || e.metaKey) {
      if (e.key.toLowerCase() === 'b') {
        e.preventDefault();
        exec('bold');
      } else if (e.key.toLowerCase() === 'i') {
        e.preventDefault();
        wrapSelectionInTag('em');
        emitChange();
      } else if (e.key.toLowerCase() === 'u') {
        e.preventDefault();
        exec('underline');
      } else if (e.key.toLowerCase() === 'k') {
        e.preventDefault();
        openLinkBar();
      }
    }
  };

  return (
    <div className="comment-editor">
      <div className="comment-editor-toolbar">
        <button type="button" className="comment-tool" title="Bold (Ctrl+B)" onMouseDown={(e) => e.preventDefault()} onClick={() => trigger('bold')}><IconBold /></button>
        <button type="button" className="comment-tool" title="Italic (Ctrl+I)" onMouseDown={(e) => e.preventDefault()} onClick={() => trigger('italic')}><IconItalic /></button>
        <button type="button" className="comment-tool" title="Underline (Ctrl+U)" onMouseDown={(e) => e.preventDefault()} onClick={() => trigger('underline')}><IconUnderline /></button>
        <button type="button" className={`comment-tool${linkOpen ? ' is-active' : ''}`} title="Link (Ctrl+K)" onMouseDown={(e) => e.preventDefault()} onClick={openLinkBar}><IconLink /></button>
        <span className="comment-tool-sep" />
        <button type="button" className="comment-tool" title="Bulleted list" onMouseDown={(e) => e.preventDefault()} onClick={() => trigger('insertUnorderedList')}><IconListBullet /></button>
        <button type="button" className="comment-tool" title="Numbered list" onMouseDown={(e) => e.preventDefault()} onClick={() => trigger('insertOrderedList')}><IconListOrdered /></button>
        <span className="comment-tool-sep" />
        <button type="button" className="comment-tool" title="Align left" onMouseDown={(e) => e.preventDefault()} onClick={() => trigger('justifyLeft')}><IconAlignLeft /></button>
        <button type="button" className="comment-tool" title="Align center" onMouseDown={(e) => e.preventDefault()} onClick={() => trigger('justifyCenter')}><IconAlignCenter /></button>
        <button type="button" className="comment-tool" title="Align right" onMouseDown={(e) => e.preventDefault()} onClick={() => trigger('justifyRight')}><IconAlignRight /></button>
        {mentionUsers.length > 0 && <span className="comment-tool-hint">Type @ to mention</span>}
      </div>
      {linkOpen && (
        <div className="comment-link-bar">
          <input
            ref={linkInputRef}
            type="url"
            value={linkUrl}
            onChange={(e) => setLinkUrl(e.target.value)}
            placeholder="https://example.com"
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                applyLink();
              }
              if (e.key === 'Escape') {
                e.preventDefault();
                closeLinkBar();
              }
            }}
          />
          {selectionCollapsed && (
            <input
              type="text"
              value={linkText}
              onChange={(e) => setLinkText(e.target.value)}
              placeholder="Link text (optional)"
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  applyLink();
                }
                if (e.key === 'Escape') {
                  e.preventDefault();
                  closeLinkBar();
                }
              }}
            />
          )}
          <button type="button" className="comment-link-apply" onMouseDown={(e) => e.preventDefault()} onClick={applyLink} disabled={!safeHref(linkUrl)}>
            Apply
          </button>
          {editingAnchor && (
            <button type="button" className="text-btn" onMouseDown={(e) => e.preventDefault()} onClick={removeLink}>
              Remove
            </button>
          )}
          <button type="button" className="text-btn" onMouseDown={(e) => e.preventDefault()} onClick={closeLinkBar}>
            Cancel
          </button>
        </div>
      )}
      <div
        ref={ref}
        className="comment-html-content comment-rich-editor-content"
        contentEditable={!disabled}
        suppressContentEditableWarning
        data-placeholder={placeholder}
        style={{ minHeight }}
        onInput={() => {
          emitChange();
          updateMentionState();
        }}
        onPaste={(e) => {
          const pasted = e.clipboardData.getData('text/plain').trim();
          const href = safeHref(pasted);
          if (href && !/\s/.test(pasted)) {
            e.preventDefault();
            const sel = window.getSelection();
            const range = sel && sel.rangeCount > 0 ? sel.getRangeAt(0) : null;
            if (range && !range.collapsed) {
              exec('createLink', href);
              ref.current?.querySelectorAll('a[href]').forEach((node) => {
                const a = node as HTMLAnchorElement;
                const safe = safeHref(a.getAttribute('href') || '');
                if (safe) decorateLink(a, safe);
              });
            } else if (range) {
              const a = document.createElement('a');
              decorateLink(a, href);
              a.textContent = pasted;
              range.insertNode(a);
              const after = document.createRange();
              after.setStartAfter(a);
              after.collapse(true);
              sel?.removeAllRanges();
              sel?.addRange(after);
            }
            emitChange();
            return;
          }
          e.preventDefault();
          document.execCommand('insertText', false, e.clipboardData.getData('text/plain'));
        }}
        onClick={(e) => {
          const editor = ref.current;
          const a = (e.target as HTMLElement).closest('a');
          if (editor && a && editor.contains(a)) e.preventDefault();
        }}
        onKeyDown={onKeyDown}
        onKeyUp={updateMentionState}
      />
      {mentionOpen && filteredMentions.length > 0 && (
        <div className="comment-mention-menu">
          {filteredMentions.map((person, idx) => (
            <button
              key={person.id}
              type="button"
              className={idx === mentionIndex ? 'is-active' : ''}
              onMouseDown={(e) => {
                e.preventDefault();
                insertMention(person);
              }}
              onMouseEnter={() => setMentionIndex(idx)}
            >
              <strong>{person.name}</strong>
              {person.email && <span>{person.email}</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default CommentRichEditor;
